import React, { useRef, useState } from 'react';
import '../styles/FileUpload.css';

interface Props {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
}

export default function FileUpload({ onFileSelect, disabled }: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    setUploadedFile(file);
    onFileSelect(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="file-upload">
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      <div
        className={`upload-area ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {uploadedFile ? (
          <div className="file-preview">
            <span className="file-icon">📎</span>
            <div className="file-info">
              <div className="file-name">{uploadedFile.name}</div>
              <div className="file-size">
                {(uploadedFile.size / 1024).toFixed(1)} KB
              </div>
            </div>
            <button
              className="file-clear"
              onClick={e => {
                e.stopPropagation();
                setUploadedFile(null);
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="upload-prompt">
            <span className="upload-icon">📁</span>
            <p>Drag file here or click to upload</p>
            <span className="upload-hint">Any file type — code, docs, images, archives</span>
          </div>
        )}
      </div>
    </div>
  );
}
